import { useEffect, useState, type FormEvent } from 'react';
import type { PlatformFeeDefaults, PlatformKey } from '@clarity/contract';
import {
  usePlatformFeeDefaults,
  useUpdatePlatformFeeDefaults,
} from '../queries/index.js';
import { QueryState } from './QueryState.js';
import { IconSettings } from '../components/icons.js';

/** Human label per platform key. */
const PLATFORM_LABEL: Record<PlatformKey, string> = {
  talabat: 'Talabat',
  careem: 'Careem',
  mythings: 'MyThings',
};

/**
 * Admin-only platform settings: the per-platform payment-gateway fee defaults
 * (onboarding Req 4.1).
 *
 * These are the "per-platform defaults" the onboarding spec refers to. They are
 * GLOBAL (one set per platform, not per client/brand). Onboarding Step 1 seeds
 * its fee inputs from the matching default; the user confirms or overrides them
 * onto the store (the store value always wins). The server enforces admin-only
 * writes (a client-role caller gets 403).
 */
export function Settings() {
  const defaults = usePlatformFeeDefaults();
  const rows: PlatformFeeDefaults[] =
    defaults.data?.status === 200 ? defaults.data.body : [];

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>Platform settings</h2>
          <p className="sub">
            Payment-gateway fee defaults per platform. Every new store inherits these; onboarding
            can override them per store.
          </p>
        </div>
      </div>

      <QueryState
        isLoading={defaults.isLoading}
        isError={defaults.isError}
        label="platform fee defaults"
      >
        {rows.length === 0 ? (
          <div className="card empty">
            <IconSettings className="empty__glyph" />
            <p>No platforms to configure.</p>
          </div>
        ) : (
          rows.map((row) => <PlatformFeeCard key={row.platformKey} row={row} />)
        )}
      </QueryState>
    </section>
  );
}

/** The number input value for an optional fee field (empty string when unset). */
function numStr(v: number | undefined): string {
  return v === undefined ? '' : String(v);
}

/**
 * One platform's editable fee-defaults card. Seeds from the stored defaults;
 * blank fields clear that default on save (the server stores "unset").
 */
function PlatformFeeCard({ row }: { row: PlatformFeeDefaults }) {
  const update = useUpdatePlatformFeeDefaults();

  const [payFeePct, setPayFeePct] = useState(numStr(row.pay_fee_pct));
  const [payFeeFixed, setPayFeeFixed] = useState(numStr(row.pay_fee_fixed));
  const [payFeeTaxPct, setPayFeeTaxPct] = useState(numStr(row.pay_fee_tax_pct));

  // Re-seed when the stored defaults change (e.g. after a successful save).
  useEffect(() => {
    setPayFeePct(numStr(row.pay_fee_pct));
    setPayFeeFixed(numStr(row.pay_fee_fixed));
    setPayFeeTaxPct(numStr(row.pay_fee_tax_pct));
  }, [row.pay_fee_pct, row.pay_fee_fixed, row.pay_fee_tax_pct]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const body = {
      ...(payFeePct.trim() === '' ? {} : { pay_fee_pct: Number(payFeePct) }),
      ...(payFeeFixed.trim() === '' ? {} : { pay_fee_fixed: Number(payFeeFixed) }),
      ...(payFeeTaxPct.trim() === '' ? {} : { pay_fee_tax_pct: Number(payFeeTaxPct) }),
    };
    update.mutate({ params: { platformKey: row.platformKey }, body });
  }

  const saved = update.data?.status === 200;

  return (
    <form
      className="card"
      onSubmit={onSubmit}
      aria-label={`${PLATFORM_LABEL[row.platformKey]} payment-gateway fee defaults`}
    >
      <h3>{`${PLATFORM_LABEL[row.platformKey]} - Online/Visa orders settings`}</h3>
      <label>
        Fee % (variable)
        <input
          type="number"
          min={0}
          max={100}
          step="any"
          value={payFeePct}
          onChange={(e) => setPayFeePct(e.target.value)}
        />
      </label>
      <label>
        Fee fixed (per order)
        <input
          type="number"
          min={0}
          step="any"
          value={payFeeFixed}
          onChange={(e) => setPayFeeFixed(e.target.value)}
        />
      </label>
      <label>
        Fee tax %
        <input
          type="number"
          min={0}
          max={100}
          step="any"
          value={payFeeTaxPct}
          onChange={(e) => setPayFeeTaxPct(e.target.value)}
        />
      </label>
      <p className="muted">Leave a field blank to clear its default.</p>
      <button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save defaults'}
      </button>
      {update.isError && (
        <p role="alert" className="error">
          Could not save the defaults.
        </p>
      )}
      {saved && !update.isPending && (
        <p role="status" className="muted">
          Saved.
        </p>
      )}
    </form>
  );
}
